import { refreshTokensAt, signInViaPopupAt, WebglSignInError, type PopupSignInTarget, type WebglSignInFailure, type WebglTokens } from "../../../auth/webgl-popup-auth.js";
import type { EmbedToken } from "./embed-host.js";

/**
 * The 3D test's own token on staging (ADR-058 D9): signed in at UNITY's Keycloak by popup, held in this closure only, handed only
 * to the embed host. Never stored, never put in a URL or a log, never sent to our API, never mixed into the app's auth seam.
 *
 * Red zone (a real token of another Keycloak). `signIn` must run inside the click (the popup needs the user activation);
 * `getToken` waits for a sign-in that is still running, refreshes below 60 s left, and answers `null` once the session is gone.
 */
export interface UnityTokenSource {
  /** Starts the popup sign-in unless one is running or a session exists. Call it synchronously from the click. */
  signIn: () => void;
  getToken: () => Promise<EmbedToken | null>;
}

const REFRESH_BELOW_S = 60;

interface Held {
  tokens: WebglTokens;
  exp: number;
  issuer: string;
  /** Our clock minus Keycloak's, measured at receipt from `iat`: Unity gets the time LEFT, not our guess of it. */
  skew: number;
}

export function createUnityTokenSource(opts: {
  target: PopupSignInTarget;
  onFailure: (reason: WebglSignInFailure) => void;
  /** Seams for tests. */
  signInAt?: typeof signInViaPopupAt;
  refreshAt?: typeof refreshTokensAt;
  now?: () => number;
}): UnityTokenSource {
  const signInAt = opts.signInAt ?? signInViaPopupAt;
  const refreshAt = opts.refreshAt ?? refreshTokensAt;
  const now = opts.now ?? (() => Date.now() / 1000);
  let held: Held | null = null;
  let pending: Promise<void> | null = null;

  const adopt = (tokens: WebglTokens) => {
    const claims = readClaims(tokens.token);
    held = claims ? { tokens, exp: claims.exp, issuer: claims.iss, skew: now() - claims.iat } : null;
    if (!held) throw new WebglSignInError("exchange-failed");
  };

  const run = (work: () => Promise<WebglTokens>, onError: (e: unknown) => void) => {
    pending = work()
      .then(adopt)
      .catch(onError)
      .finally(() => {
        pending = null;
      });
  };

  return {
    signIn: () => {
      if (pending || held) return;
      run(
        () => signInAt(opts.target),
        (e) => {
          held = null;
          opts.onFailure(e instanceof WebglSignInError ? e.reason : "exchange-failed");
        },
      );
    },
    getToken: async () => {
      if (pending) await pending;
      if (held && held.exp - (now() - held.skew) < REFRESH_BELOW_S) {
        const refreshToken = held.tokens.refreshToken;
        run(
          () => refreshAt(opts.target, refreshToken),
          () => {
            held = null; // refused: the session is gone, a new click signs in again
          },
        );
        await pending;
      }
      if (!held) return null;
      const expiresIn = Math.floor(held.exp - (now() - held.skew));
      return expiresIn > 0 ? { accessToken: held.tokens.token, expiresIn, issuer: held.issuer } : null;
    },
  };
}

/** `exp`, `iat`, `iss` from the JWT payload (base64url, UTF-8). Only read to time the token; Unity checks the issuer itself. */
function readClaims(jwt: string): { exp: number; iat: number; iss: string } | null {
  const part = jwt.split(".")[1];
  if (!part) return null;
  try {
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(part.length / 4) * 4, "=");
    const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
    const c = JSON.parse(new TextDecoder().decode(bytes)) as { exp?: unknown; iat?: unknown; iss?: unknown };
    if (typeof c.exp !== "number" || typeof c.iss !== "string") return null;
    return { exp: c.exp, iat: typeof c.iat === "number" ? c.iat : c.exp - 300, iss: c.iss };
  } catch {
    return null;
  }
}
